/**
 * Tracers.js — velocity-aligned tracer rounds.
 * owner: fx
 *
 * A tracer is not a line from the muzzle to the impact: it is a short, bright
 * slug of burning compound travelling at ~900 m/s. It is stretched along its
 * direction of travel, has a hot core, a dimmer halo and a bright head, and it
 * dies when it reaches the impact point.
 *
 * One instanced draw call for every live tracer.
 */
import * as THREE from 'three';
import { DEPTH_HELPERS } from './Shaders.js';

const TRACER_VS = /* glsl */`
precision highp float;
attribute vec4 iFrom;    // xyz origin, w length of the flight
attribute vec4 iDir;     // xyz direction, w speed
attribute vec4 iParams;  // age, life, width, tailLength

varying vec2 vUv;
varying float vFade, vViewZ, vHead;

void main(){
  float age = iParams.x;
  if (age < 0.0 || age >= iParams.y){ gl_Position = vec4(0.0, 0.0, -3.0, 1.0); return; }

  float travelled = min(age * iDir.w, iFrom.w);
  float remain = iFrom.w - travelled;
  vec3 head = iFrom.xyz + iDir.xyz * travelled;

  // the slug shortens as it runs out of flight path so it never overshoots
  float tail = min(iParams.w, travelled + 0.15);
  vec3 back = head - iDir.xyz * tail;

  vec3 mid = mix(back, head, 0.5);
  vec4 mv = viewMatrix * vec4(mid, 1.0);
  vec3 headV = (viewMatrix * vec4(head, 1.0)).xyz;
  vec3 backV = (viewMatrix * vec4(back, 1.0)).xyz;

  vec2 axis = headV.xy - backV.xy;
  float len = length(axis);
  vec2 dir = len > 1e-4 ? axis / len : vec2(0.0, 1.0);
  vec2 nrm = vec2(dir.y, -dir.x);

  vec2 corner = position.xy;
  vec3 vpos = mv.xyz
            + vec3(dir, 0.0) * corner.y * max(len, 0.02)
            + vec3(nrm, 0.0) * corner.x * iParams.z;

  gl_Position = projectionMatrix * vec4(vpos, 1.0);
  vUv = uv;
  float fadeIn  = clamp(age / 0.012, 0.0, 1.0);
  float fadeOut = clamp(remain / 1.2, 0.0, 1.0) * clamp((iParams.y - age) / 0.05, 0.0, 1.0);
  vFade = fadeIn * fadeOut;
  vViewZ = -vpos.z;
  vHead = 1.0;
}
`;

const TRACER_FS = /* glsl */`
precision highp float;
${DEPTH_HELPERS}
uniform vec2 uResolution;
uniform float uHasDepth;
uniform vec3 uColor;

varying vec2 vUv;
varying float vFade, vViewZ, vHead;

void main(){
  float x = abs(vUv.x * 2.0 - 1.0);
  float y = vUv.y;                      // 0 = tail, 1 = head

  float core = pow(clamp(1.0 - x, 0.0, 1.0), 12.0);
  float halo = pow(clamp(1.0 - x, 0.0, 1.0), 2.2) * 0.30;
  float along = pow(y, 1.6);            // brightest at the head, tapering back
  float headBlob = exp(-(pow((y - 0.97) * 22.0, 2.0) + pow(x * 5.5, 2.0)));

  float a = (core * along + halo * along * 0.8 + headBlob * 1.3) * vFade;
  if (a <= 0.004) discard;

  vec3 col = uColor * (0.55 + 1.9 * core + 3.0 * headBlob);

  if (uHasDepth > 0.5){
    float sceneZ = fxSceneZ(gl_FragCoord.xy / uResolution);
    a *= clamp((sceneZ - vViewZ) / 0.12, 0.0, 1.0);
  }

  vec4 outColor = vec4(col, clamp(a, 0.0, 1.0));
  #if defined( TONE_MAPPING )
    outColor.rgb = toneMapping(outColor.rgb);
  #endif
  outColor = linearToOutputTexel(outColor);
  gl_FragColor = outColor;
}
`;

export class TracerSystem {
  constructor(capacity = 96) {
    this.capacity = capacity;
    this.head = 0;
    this.iFrom = new Float32Array(capacity * 4);
    this.iDir = new Float32Array(capacity * 4);
    this.iParams = new Float32Array(capacity * 4);
    for (let i = 0; i < capacity; i++) this.iParams[i * 4] = -1;

    const g = new THREE.InstancedBufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute([
      -0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0,
    ], 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute([0, 0, 1, 0, 1, 1, 0, 1], 2));
    g.setIndex([0, 1, 2, 0, 2, 3]);
    this.aFrom = new THREE.InstancedBufferAttribute(this.iFrom, 4).setUsage(THREE.DynamicDrawUsage);
    this.aDir = new THREE.InstancedBufferAttribute(this.iDir, 4).setUsage(THREE.DynamicDrawUsage);
    this.aParams = new THREE.InstancedBufferAttribute(this.iParams, 4).setUsage(THREE.DynamicDrawUsage);
    g.setAttribute('iFrom', this.aFrom);
    g.setAttribute('iDir', this.aDir);
    g.setAttribute('iParams', this.aParams);
    g.instanceCount = capacity;
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);

    this.uniforms = {
      tDepth: { value: null }, tNormal: { value: null }, uDepthMode: { value: 0 },
      uResolution: { value: new THREE.Vector2(1, 1) },
      uHasDepth: { value: 0 },
      uColor: { value: new THREE.Color(6.5, 2.2, 0.55) },
    };

    const mat = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader: TRACER_VS,
      fragmentShader: TRACER_FS,
      transparent: true,
      depthTest: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    });
    this.mesh = new THREE.Mesh(g, mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 3050;
    this.mesh.name = 'fx_tracers';
    this.mesh.userData.noCollide = true;
    this.mesh.userData.fx = true;
    this.geometry = g;
    this.live = 0;
  }

  /** @param {THREE.Vector3} from @param {THREE.Vector3} to */
  add(from, to, o = {}) {
    const i = this.head;
    this.head = (this.head + 1) % this.capacity;
    _d.subVectors(to, from);
    const dist = _d.length();
    if (dist < 0.05) return;
    _d.multiplyScalar(1 / dist);
    const speed = o.speed ?? 780;
    const i4 = i * 4;
    this.iFrom[i4] = from.x; this.iFrom[i4 + 1] = from.y; this.iFrom[i4 + 2] = from.z;
    this.iFrom[i4 + 3] = dist;
    this.iDir[i4] = _d.x; this.iDir[i4 + 1] = _d.y; this.iDir[i4 + 2] = _d.z;
    this.iDir[i4 + 3] = speed;
    this.iParams[i4] = 0;
    this.iParams[i4 + 1] = dist / speed + 0.05;
    this.iParams[i4 + 2] = o.width ?? 0.045;
    this.iParams[i4 + 3] = o.length ?? 2.4;
    this._dirty = true;
  }

  update(dt, env) {
    let live = 0;
    for (let i = 0; i < this.capacity; i++) {
      const a = this.iParams[i * 4];
      if (a < 0) continue;
      const next = a + dt;
      if (next >= this.iParams[i * 4 + 1]) { this.iParams[i * 4] = -1; }
      else { this.iParams[i * 4] = next; live++; }
      this._dirty = true;
    }
    this.live = live;
    if (this._dirty) {
      this.aFrom.needsUpdate = true;
      this.aDir.needsUpdate = true;
      this.aParams.needsUpdate = true;
      this._dirty = false;
    }
    this.uniforms.tDepth.value = env.depthTexture;
    this.uniforms.tNormal.value = env.normalTexture || env.depthTexture;
    this.uniforms.uDepthMode.value = env.depthMode;
    this.uniforms.uHasDepth.value = env.depthTexture ? 1 : 0;
    this.uniforms.uResolution.value.copy(env.resolution);
    this.mesh.visible = live > 0;
  }

  reset() {
    for (let i = 0; i < this.capacity; i++) this.iParams[i * 4] = -1;
    this.head = 0;
    this._dirty = true;
    this.live = 0;
  }

  dispose() { this.geometry.dispose(); this.mesh.material.dispose(); }
}

const _d = new THREE.Vector3();
